import {observer} from 'mobx-react-lite'
import * as React from 'react'
import {useEffect, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Debouncer, HTMLUtil, TypeUtil} from 'yougile-util'
import {DataObjectId, Hub, systemStickers, Tag} from 'yougile-model'
import {Sync} from '@client/sync'
import {Backend} from '@common/backend'
import {LinkButton} from '@components/common/base/buttons'
import {ActionsPanel} from '@components/common/base/buttons/actions-panel/actions-panel'
import {Icon} from '@components/common/base/icon'
import {Input} from '@components/common/base/input'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {LinkButtonWrapped} from '@components/movement-templates/link-button-wrapped'
import {TemplateChatSubscribers} from '@components/right-panel/chat/template-chat-subscribers'
import {ChecklistsTab} from '@components/right-panel/checklists-tab/checklists-tab'
import {TaskStickers} from '@components/sticker/task-stickers'
import {
  useBoard,
  useForceUpdate,
  useGlobalKeyDown,
  useInputKeyDown
} from '@containers/hooks'
import {Sticker} from '@extensions/sticker/sticker'
import {SubtaskUtil} from '@extensions/subtask-util'
import {taskPhantomObserver} from '@extensions/task-phantom-observer'
import {filterCustomStickers} from '@extensions/task-templates/filter-custom-stickers'
import {ExecutorRow} from './executor-row'
import {getArray} from './helpers'
import {ResponsibleRow} from './responsible-row'
import {TaskDescription} from './task-description'
import {TaskDescriptionStore} from './task-description/task-description-store'
import {useTaskTemplatesStore} from './task-templates-store'

const TwMainContainer = tw.div`
  flex
  flex-col
  h-full
  relative
  pb-[80px]
`

const TwTitle = tw.div`
  flex
  items-center
  text-primary
  text-sm-semibold
  h-32
  mb-8
  cursor-default
  shrink-0
`

export const TwTitleSecondary = tw.div<{$noGap?: boolean}>`
  flex
  items-center
  text-primary
  text-sm-semibold
  pt-8
  pb-4
  h-28
  cursor-default
  shrink-0
  ${p => (p.$noGap ? '' : 'mb-4')}
`

const TwDescription = tw.div`
  break-words
`

const triggerDebouncer = new Debouncer(700)

export const TaskTemplatesEdit = observer(() => {
  const [state, store] = useTaskTemplatesStore()
  const forceUpdate = useForceUpdate()
  const inputRef = useRef<HTMLInputElement | undefined>()
  const [emptyStickers, setEmptyStickers] = useState(false)
  const [canBeSubmitted, setCanBeSubmitted] = useState(false)

  const board = useBoard()
  const box = Sync.getCurrentSandbox()
  const project = box.getHub(board.location)

  const hub = store.template

  const [descriptionStore] = useState(() => new TaskDescriptionStore(hub))

  useEffect(() => {
    if (!hub) {
      return
    }

    SubtaskUtil.updateCaches(hub.id)
  }, [hub?.id, hub?.data?.subtaskList?.order.length])

  useEffect(() => {
    setTimeout(() => {
      inputRef.current?.focus()
      const val = inputRef.current?.value
      if (val && state.createFromExist) {
        inputRef.current?.setSelectionRange(0, val.length)
      }
    }, 10)
  }, [state.editId])

  triggerDebouncer.runButNotOften(() => {
    const val = store.canSubmit()
    setCanBeSubmitted(val)
  })

  const lang = Lang.msg.taskTemplates

  useEffect(() => {
    forceUpdate()
  }, [taskPhantomObserver.modified])

  useGlobalKeyDown({
    escape: store.cancel,
    enter: store.save
  })

  useInputKeyDown({ref: inputRef, save: store.save, cancel: store.cancel})

  const executors = getArray<DataObjectId>(hub?.stickers[systemStickers.User])
  const assignedBy = Object.keys(hub?.data?.assignedBy || {}) as DataObjectId[]

  const descriptionDangerouslyHTML = HTMLUtil.sanitizeHtml(
    hub?.description || ''
  )

  const pinnedTags = filterCustomStickers(
    getPinnedStickers(hub).filter(TypeUtil.isTruthy)
  )
  const supportSubtasks = Backend.features?.taskTemplatesSubtasks

  return (
    <TwMainContainer className="pb-60">
      <LinkButtonWrapped
        icon={'IconCustomArrowLeft'}
        onMouseDown={store.cancel}
        text={Lang.msg.taskTemplates.backToList}
        height={'h-32'}
        className={'mb-8 shrink-0'}
      />

      <TwTitle>
        {!state.editId
          ? lang.createTitle
          : lang.editTitle(store.getTitle() || '')}
      </TwTitle>

      <WithPerfectScrollbar
        className={'ml-[-8px] mr-[-16px] pl-8 pr-16 flex flex-col gap-24'}
      >
        <div>
          <TwTitleSecondary>{lang.templateName}</TwTitleSecondary>
          <Input
            ref={inputRef}
            value={store.getTitle()}
            onChange={({text}) => store.setTitle(text)}
            placeholder={lang.createTitlePlaceholder}
            size="32"
            className="w-full"
          />
        </div>

        <div>
          <TwTitleSecondary>{lang.titleAuthor}</TwTitleSecondary>
          <ExecutorRow
            text={lang.executors}
            values={executors}
            hub={hub}
            project={project}
          />
          <ResponsibleRow
            text={lang.responsible}
            values={assignedBy}
            hub={hub}
            project={project}
          />
        </div>

        {!emptyStickers && (
          <div className="group">
            <TwTitleSecondary>{lang.stickers}</TwTitleSecondary>

            {pinnedTags?.some(t => t.title === 'Deadline') && (
              <div className="flex items-start gap-4 mb-8 cursor-default pb-4">
                <Icon icon="IconCustomAlertCircle" color="status-warning" />
                <div className="text-sm-regular text-secondary">
                  {Lang.msg.taskTemplates.deadlineHint}
                </div>
              </div>
            )}

            <TaskStickers
              task={hub}
              pinnedTags={pinnedTags}
              isParentTaskTemplate={true}
              cbEmptyStickers={() => setEmptyStickers(true)}
              showButtonAlways
            />
          </div>
        )}

        <div className="flex flex-col gap-4">
          <TwTitleSecondary $noGap>{lang.description}</TwTitleSecondary>

          <LinkButton
            icon={'IconCustomEdit'}
            className={'h-24'}
            iconPadding={'4'}
            onClick={() => {
              descriptionStore.setEdit(true)
            }}
            noPaddings
          >
            {lang.editDescription}
          </LinkButton>

          <TwDescription
            dangerouslySetInnerHTML={{
              __html: descriptionDangerouslyHTML
            }}
          />

          {descriptionStore.state.edit && (
            <TaskDescription hub={hub} store={descriptionStore} />
          )}
        </div>

        <ChecklistsTab hub={hub} newBg={true} hideSubtasks={!supportSubtasks} />

        <div>
          <TemplateChatSubscribers hub={hub} project={project} />
        </div>
      </WithPerfectScrollbar>

      <ActionsPanel
        className="!bottom-8"
        show={canBeSubmitted}
        primaryClick={store.save}
        primaryText={state.editId ? lang.saveButton : lang.createButton}
        secondaryText={Lang.msg.cancel}
        secondaryClick={store.cancelForce}
        error={state.errorHasChanges}
        errorText={Lang.msg.errorNotSaveData}
        errorBg={state.errorHasChangesBgRed}
      />
    </TwMainContainer>
  )
})

const getPinnedStickers = (task?: Hub): Tag[] => {
  return (task && Sticker.getAllPinned(task)) || []
}
